const BRAND_STORY_TEXT_MIN = 8;

class BrandStorySection extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector(".brand-story__grid");
    this.inset = this.querySelector("[data-inset]");
    this.copy = this.querySelector("[data-copy]");
    this.content = this.querySelector(".brand-story__content");
    this._fitRaf = null;
    this._fitRetries = 0;
    this._abort = new AbortController();

    this.scheduleFit();

    const { signal } = this._abort;
    window.addEventListener("resize", () => this.scheduleFit(), { signal });

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", () => this.scheduleFit(), { signal });
      window.visualViewport.addEventListener("scroll", () => this.scheduleFit(), { signal });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.scheduleFit());
    }

    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => this.scheduleFit());
      if (this.grid) this._resizeObserver.observe(this.grid);
      if (this.inset) this._resizeObserver.observe(this.inset);
    }
  }

  disconnectedCallback() {
    this._abort.abort();
    this._resizeObserver?.disconnect();
    if (this.copy) this.copy.style.maxHeight = "";
  }

  isMobile() {
    return window.matchMedia("(min-width: 990px)").matches === false;
  }

  readCssPx(varName, fallback) {
    const raw = getComputedStyle(this).getPropertyValue(varName).trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  clearFitVars() {
    [
      "--brand-story-heading-fit-d",
      "--brand-story-heading-fit-m",
      "--brand-story-text-fit-d",
      "--brand-story-text-fit-m",
      "--brand-story-cta-fit-d",
      "--brand-story-cta-fit-m",
    ].forEach((name) => this.style.removeProperty(name));
  }

  getFitVars() {
    if (this.isMobile()) {
      return {
        headingMax: Math.round(this.readCssPx("--brand-story-heading-max-m", 18)),
        textMax: Math.round(this.readCssPx("--brand-story-text-max-m", 13)),
        headingFit: "--brand-story-heading-fit-m",
        textFit: "--brand-story-text-fit-m",
        ctaFit: "--brand-story-cta-fit-m",
      };
    }

    return {
      headingMax: Math.round(this.readCssPx("--brand-story-heading-max-d", 20)),
      textMax: Math.round(this.readCssPx("--brand-story-text-max-d", 13)),
      headingFit: "--brand-story-heading-fit-d",
      textFit: "--brand-story-text-fit-d",
      ctaFit: "--brand-story-cta-fit-d",
    };
  }

  setFitSizes(headingSize, textSize, headingFit, textFit, ctaFit) {
    const ctaSize = Math.max(BRAND_STORY_TEXT_MIN, Math.round(textSize * 0.82));
    this.style.setProperty(headingFit, headingSize + "px");
    this.style.setProperty(textFit, textSize + "px");
    this.style.setProperty(ctaFit, ctaSize + "px");
  }

  getInsetBox() {
    if (!this.inset) return { width: 0, height: 0 };

    return {
      width: this.inset.clientWidth,
      height: this.inset.clientHeight,
    };
  }

  applyCopyMaxHeight(boxHeight) {
    if (!this.copy || boxHeight < 1) return;
    this.copy.style.maxHeight = Math.floor(boxHeight) + "px";
  }

  copyFits(boxWidth, boxHeight) {
    if (!this.copy) return true;
    if (boxWidth < 8 || boxHeight < 8) return false;

    return (
      this.copy.scrollWidth <= boxWidth + 1 &&
      this.copy.scrollHeight <= boxHeight + 1
    );
  }

  scheduleFit() {
    if (this._fitRaf != null) return;
    this._fitRaf = requestAnimationFrame(() => {
      this._fitRaf = null;
      this.fitAll();
    });
  }

  fitAll() {
    if (!this.copy || !this.inset) return;

    const { headingMax, textMax, headingFit, textFit, ctaFit } = this.getFitVars();
    const minSize = BRAND_STORY_TEXT_MIN;

    this.clearFitVars();
    if (this.copy) this.copy.style.maxHeight = "";
    void this.offsetHeight;

    const box = this.getInsetBox();
    if (box.height < 8) {
      if (this._fitRetries < 8) {
        this._fitRetries += 1;
        requestAnimationFrame(() => this.fitAll());
      }
      return;
    }
    this._fitRetries = 0;

    for (let headingSize = headingMax; headingSize >= minSize; headingSize -= 1) {
      const textCap = Math.min(textMax, headingSize);
      for (let textSize = textCap; textSize >= minSize; textSize -= 1) {
        this.setFitSizes(headingSize, textSize, headingFit, textFit, ctaFit);
        this.applyCopyMaxHeight(box.height);
        void this.offsetHeight;

        if (this.copyFits(box.width, box.height)) return;
      }
    }

    this.setFitSizes(minSize, minSize, headingFit, textFit, ctaFit);
    this.applyCopyMaxHeight(box.height);
  }
}

if (!customElements.get("brand-story-section")) {
  customElements.define("brand-story-section", BrandStorySection);
}
